/**
 * stagePipelineModel.ts — Admin > Stage Pipeline's shared shapes and helpers:
 * query keys, the CRM caches every write refreshes (KTD14), pipeline order,
 * the pill colours (the plan progress bar's), and the delete-blocker wording.
 */
import type {
  PipelineStage,
  StageBlocker,
  StageBlockerKind,
  StageRule,
  StageRules,
  StagePipelineState,
} from '@/services/stagePipelineAdmin.service';

// ─── Query keys ───────────────────────────────────────────────────────────────

/** Every query of this page starts with it, so one prefix refreshes them all. */
export const STAGE_PIPELINE_QUERY_ROOT = 'stage-pipeline';

export const stagePipelineKeys = {
  state: (pipelineTypeId: number) => [STAGE_PIPELINE_QUERY_ROOT, 'state', pipelineTypeId] as const,
  rules: (fromStageId: number | null, pipelineTypeId: number) => [STAGE_PIPELINE_QUERY_ROOT, 'rules', fromStageId, pipelineTypeId] as const,
  automations: (stageId: number | null) => [STAGE_PIPELINE_QUERY_ROOT, 'automations', stageId] as const,
};

/**
 * The screens that read stages, order and rules: the CRM board's columns and
 * plans, its pipeline map, and every plan's progress bar and status picker.
 * Refreshed by prefix after any write (R28).
 */
export const CRM_CACHE_PREFIXES = ['crm-stages', 'crm-plans', 'crm-pipeline-stage-map', 'plan-progress-bar', 'plan-stage-subs'] as const;

// ─── Order ────────────────────────────────────────────────────────────────────

/** Pipeline order: Inquiry first, then the saved order. */
export function byPipelineOrder(a: PipelineStage, b: PipelineStage): number {
  if (a.isInquiry !== b.isInquiry) return a.isInquiry ? -1 : 1;
  return a.order - b.order || a.id - b.id;
}

/** The page state with the live stages after Inquiry in `ids` order (orderNum from 2, as the API writes it). */
export function withStageOrder(state: StagePipelineState, ids: number[]): StagePipelineState {
  return {
    ...state,
    stages: state.stages.map((s) => {
      const i = ids.indexOf(s.id);
      return i < 0 ? s : { ...s, order: i + 2 };
    }),
  };
}

// ─── Rules ────────────────────────────────────────────────────────────────────

/** A missing rule reads as the progress bar reads it: no move, arrow, shown. */
export function defaultRule(toStageId: number): StageRule {
  return { toStageId, moveTo: false, showArrow: true, hidden: false };
}

export function withRule(rules: StageRules, toStageId: number, patch: Partial<Omit<StageRule, 'toStageId'>>): StageRules {
  const has = rules.rules.some((r) => r.toStageId === toStageId);
  const list = has ? rules.rules : [...rules.rules, defaultRule(toStageId)];
  return { ...rules, rules: list.map((r) => (r.toStageId === toStageId ? { ...r, ...patch } : r)) };
}

/** One pill: a live stage as a plan in the selected stage sees it. */
export interface PillModel {
  stage: PipelineStage;
  rule: StageRule;
  /** The selected stage: fixed in place, no "move to", never hidden. */
  selected: boolean;
}

export type PillTone = 'selected' | 'move-to' | 'default';

export function pillTone(p: PillModel): PillTone {
  if (p.selected) return 'selected';
  return p.rule.moveTo ? 'move-to' : 'default';
}

/** The plan progress bar's colours (PlanProgressBar.tsx; legacy #3a7fd5 / #888 / #bbb, white text). */
export const PILL_TONE_CLASS: Record<PillTone, string> = {
  selected: 'bg-[#3a7fd5]',
  'move-to': 'bg-[#888]',
  default: 'bg-[#bbb]',
};

export function pillLabel(stage: PipelineStage): string {
  return `${stage.order} ${stage.name}`;
}

// ─── Delete blockers ──────────────────────────────────────────────────────────

const BLOCKER_NOUN: Record<StageBlockerKind, string> = {
  task: 'Task',
  taskGroupItem: 'Task group item',
  taskGroupStageTrigger: 'Task group stage trigger',
  taskGroupStatusTrigger: 'Task group status trigger',
  taskGroupStart: 'Task group start',
  taskGroupStop: 'Task group stop',
  appointmentTypeGroup: 'Appointment type group',
  appointmentStageRule: 'Appointment stage rule',
};

/**
 * Why a stage can't be deleted, or null when nothing points at it: every live
 * automation by name, then the pending plan tasks as a count (R21).
 */
export function stageDeleteBlock(blockers: StageBlocker[], pendingPlanTasks: number): string | null {
  const parts: string[] = [];
  if (blockers.length > 0) {
    parts.push(`in use by ${blockers.map((b) => `${BLOCKER_NOUN[b.kind] ?? 'Automation'} "${b.name}"`).join(', ')}`);
  }
  if (pendingPlanTasks > 0) {
    parts.push(`${pendingPlanTasks} pending plan ${pendingPlanTasks === 1 ? 'task points' : 'tasks point'} at it`);
  }
  return parts.length > 0 ? `Can't delete: ${parts.join('; ')}. Re-point them first.` : null;
}
